import React from 'react';

const TokenUsage = ({ tokenUsage, isConnected, isVoiceOnly = false, elapsedTime = 0 }) => {
  // Pricing per million tokens (based on your provided rates)
  const PRICING = {
    inputTokens: 2.50,      // $2.50 per million tokens
    outputTokens: 10.00,    // $10.00 per million tokens
    inputAudioTokens: 40.00,  // $40.00 per million tokens
    outputAudioTokens: 80.00  // $80.00 per million tokens
  };

  const calculateCost = (tokens, pricePerMillion) => {
    return (tokens / 1000000) * pricePerMillion;
  };

  const formatCost = (cost) => {
    if (cost < 0.001) {
      return `$${(cost * 1000000).toFixed(2)}µ`; // Show in millionths for very small amounts
    } else if (cost < 1) {
      return `$${cost.toFixed(4)}`; // Show 4 decimal places for amounts under $1
    } else {
      return `$${cost.toFixed(2)}`; // Show 2 decimal places for larger amounts
    }
  };

  const formatTokens = (tokens) => {
    if (tokens < 1000) {
      return tokens.toString();
    } else if (tokens < 1000000) {
      return `${(tokens / 1000).toFixed(1)}K`;
    } else {
      return `${(tokens / 1000000).toFixed(2)}M`;
    }
  };

  const formatElapsedTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }
  };

  const totalCost = 
    calculateCost(tokenUsage.inputTokens, PRICING.inputTokens) +
    calculateCost(tokenUsage.outputTokens, PRICING.outputTokens) +
    calculateCost(tokenUsage.inputAudioTokens, PRICING.inputAudioTokens) +
    calculateCost(tokenUsage.outputAudioTokens, PRICING.outputAudioTokens);

  return (
    <div className="token-usage">
      <h4>💰 Token Usage & Cost</h4>
      
      <div className="usage-grid">
        <div className="usage-row">
          <div className="usage-type">
            <span className="usage-icon">📝</span>
            <span>Text Input</span>
          </div>
          <div className="usage-stats">
            <span className="token-count">{formatTokens(tokenUsage.inputTokens)}</span>
            <span className="token-cost">{formatCost(calculateCost(tokenUsage.inputTokens, PRICING.inputTokens))}</span>
          </div>
        </div>

        <div className="usage-row">
          <div className="usage-type">
            <span className="usage-icon">💬</span>
            <span>Text Output</span>
          </div>
          <div className="usage-stats">
            <span className="token-count">{formatTokens(tokenUsage.outputTokens)}</span>
            <span className="token-cost">{formatCost(calculateCost(tokenUsage.outputTokens, PRICING.outputTokens))}</span>
          </div>
        </div>

        <div className="usage-row">
          <div className="usage-type">
            <span className="usage-icon">🎤</span>
            <span>Audio Input</span>
          </div>
          <div className="usage-stats">
            <span className="token-count">{formatTokens(tokenUsage.inputAudioTokens)}</span>
            <span className="token-cost">{formatCost(calculateCost(tokenUsage.inputAudioTokens, PRICING.inputAudioTokens))}</span>
          </div>
        </div>

        <div className="usage-row">
          <div className="usage-type">
            <span className="usage-icon">🔊</span>
            <span>Audio Output</span>
          </div>
          <div className="usage-stats">
            <span className="token-count">{formatTokens(tokenUsage.outputAudioTokens)}</span>
            <span className="token-cost">{formatCost(calculateCost(tokenUsage.outputAudioTokens, PRICING.outputAudioTokens))}</span>
          </div>
        </div>

        <div className="usage-row total-row">
          <div className="usage-type">
            <span className="usage-icon">💲</span>
            <span><strong>Total Session</strong></span>
          </div>
          <div className="usage-stats">
            <span className="token-count">
              {formatTokens(
                tokenUsage.inputTokens + 
                tokenUsage.outputTokens + 
                tokenUsage.inputAudioTokens + 
                tokenUsage.outputAudioTokens
              )}
            </span>
            <span className="token-cost total-cost">{formatCost(totalCost)}</span>
          </div>
        </div>

        <div className="usage-row elapsed-time-row">
          <div className="usage-type">
            <span className="usage-icon">🕐</span>
            <span><strong>Elapsed Time</strong></span>
          </div>
          <div className="usage-stats">
            <span className="token-count"></span>
            <span className="token-cost elapsed-time">{formatElapsedTime(elapsedTime)}</span>
          </div>
        </div>
      </div>

      {isConnected && (
        <div className="usage-indicator">
          <div className="pulse-dot"></div>
          <span>Live tracking active</span>
          {isVoiceOnly && (
            <span className="voice-mode-indicator">• Voice-only mode</span>
          )}
        </div>
      )}

      <div className="usage-rates">
        <small>
          Rates: Text ${PRICING.inputTokens}/${PRICING.outputTokens} • Audio ${PRICING.inputAudioTokens}/${PRICING.outputAudioTokens} per million tokens
          {isVoiceOnly && (
            <><br />ℹ️ In voice-only mode, API tokens are automatically categorized as audio tokens</>
          )}
        </small>
      </div>
    </div>
  );
};

export default TokenUsage;